import React, { useState, useEffect } from 'react';
import TrialGate from './TrialGate';
import {
  removeMember, updateMemberRole,
  getCompanyMembers, getOrCreateCompany,
  createCompanyInvite, getCompanyInvites, revokeCompanyInvite,
  logActivity,
} from './db';
import { getSeatLimit } from './companyAccountDb';
import { supabase } from './supabase';

// ── Constants ─────────────────────────────────────────────────────────────────

const ROLE_META = {
  owner:   { label: 'Owner',   color: '#f59e0b',    bg: 'rgba(245,158,11,0.12)',  border: 'rgba(245,158,11,0.3)'  },
  admin:   { label: 'Admin',   color: '#a78bfa',    bg: 'rgba(167,139,250,0.12)', border: 'rgba(167,139,250,0.3)' },
  member:  { label: 'Member',  color: 'var(--accent)', bg: 'var(--blue-bg)',       border: 'rgba(45,125,210,0.25)' },
  auditor: { label: 'Auditor', color: 'var(--text3)', bg: 'var(--surface2)',       border: 'var(--border)'         },
};

const ROLE_DESCRIPTIONS = {
  admin:   'Can invite members, manage roles, and access all compliance data. Cannot manage billing.',
  member:  'Can work on compliance data. Cannot invite members or view billing.',
  auditor: 'Read-only access to reports and evidence. Cannot edit anything.',
};

const inputStyle = {
  width: '100%', padding: '9px 12px', borderRadius: 7, fontSize: 13,
  background: 'var(--bg2)', border: '1px solid var(--border)', color: 'var(--text)',
  outline: 'none', boxSizing: 'border-box',
};

// ── Small components ──────────────────────────────────────────────────────────

function RoleBadge({ role }) {
  const m = ROLE_META[role] || ROLE_META.member;
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, fontFamily: 'var(--mono)', textTransform: 'uppercase',
      letterSpacing: '0.04em', padding: '2px 7px', borderRadius: 6,
      color: m.color, background: m.bg, border: `1px solid ${m.border}`,
    }}>
      {m.label}
    </span>
  );
}

function StatusBadge({ active }) {
  return (
    <span style={{
      fontSize: 10, fontWeight: 500, padding: '2px 7px', borderRadius: 6,
      color: active ? 'var(--green)' : 'var(--amber)',
      background: active ? 'var(--green-bg)' : 'rgba(245,158,11,0.1)',
      border: `1px solid ${active ? 'rgba(29,184,122,0.3)' : 'rgba(245,158,11,0.3)'}`,
    }}>
      {active ? 'Active' : 'Pending'}
    </span>
  );
}

function SeatBar({ used, limit }) {
  const pct = Math.min(100, Math.round((used / Math.max(limit, 1)) * 100));
  const color = pct >= 100 ? 'var(--red)' : pct >= 80 ? 'var(--amber)' : 'var(--green)';
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>
        <span style={{ fontWeight: 600, color }}>
          {used} / {limit === 999 ? '∞' : limit} seats used
        </span>
        <span>{pct >= 100 ? 'Full' : `${limit - used} remaining`}</span>
      </div>
      <div style={{ height: 5, background: 'var(--surface2)', borderRadius: 999, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: 999, transition: 'width 0.3s' }} />
      </div>
    </div>
  );
}

function Avatar({ name, size = 32 }) {
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%', flexShrink: 0,
      background: 'linear-gradient(135deg, #2D7DD2, #0D3F73)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: Math.round(size * 0.38), fontWeight: 700, color: '#fff',
    }}>
      {(name || '?').charAt(0).toUpperCase()}
    </div>
  );
}

// ── Invite Modal ───────────────────────────────────────────────────────────────

function InviteModal({ company, session, profile, seatsFull, onClose, onInvited }) {
  const [email, setEmail]         = useState('');
  const [role, setRole]           = useState('member');
  const [inviting, setInviting]   = useState(false);
  const [error, setError]         = useState('');
  const [inviteLink, setInviteLink] = useState('');
  const [copied, setCopied]       = useState(false);
  const [emailSent, setEmailSent] = useState(null); // null | 'sent' | 'failed'

  async function handleSubmit(e) {
    e.preventDefault();
    if (!email.trim()) { setError('Enter an email address.'); return; }
    if (seatsFull) { setError('Seat limit reached. Upgrade your plan to invite more members.'); return; }
    setInviting(true);
    setError('');

    const result = await createCompanyInvite(company.id, email.trim(), role, session?.user?.id, []);
    if (!result) {
      setError('Failed to create invite. Please try again.');
      setInviting(false);
      return;
    }

    const link = `${window.location.origin}?company_invite=${result.token}`;
    setInviteLink(link);
    logActivity(session?.user?.id, company.id, 'member_invited', 'user', { email: email.trim(), role });
    onInvited();

    // Try to send email (non-blocking)
    try {
      const res = await fetch(
        `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/send-invite`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}` },
          body: JSON.stringify({
            recipientEmail: email.trim(),
            companyName: company.name,
            companyCode: company.company_code || null,
            inviteLink: link,
            senderName: profile?.company_name || profile?.email || '',
          }),
        }
      );
      const data = await res.json();
      setEmailSent(data.success ? 'sent' : 'failed');
    } catch {
      setEmailSent('failed');
    }
    setInviting(false);
  }

  function handleCopy() {
    navigator.clipboard.writeText(inviteLink).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  }

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{
        background: 'var(--surface)', border: '1px solid var(--border)',
        borderRadius: 14, padding: '28px 28px 24px', width: '100%', maxWidth: 460,
        boxShadow: '0 24px 64px rgba(0,0,0,0.35)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <div style={{ fontSize: 17, fontWeight: 700 }}>Invite Team Member</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text3)', fontSize: 20, padding: 0 }}>×</button>
        </div>

        {inviteLink ? (
          <div>
            <div style={{ textAlign: 'center', marginBottom: 20 }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>🎉</div>
              <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>Invite Created!</div>
              <div style={{ fontSize: 13, color: 'var(--text3)' }}>
                Share this link with {email}.{emailSent === 'sent' ? ' We also sent them an email.' : ''}
              </div>
            </div>
            <div style={{
              background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 8,
              padding: '10px 12px', fontSize: 11, fontFamily: 'var(--mono)', wordBreak: 'break-all',
              color: 'var(--text3)', marginBottom: 14,
            }}>
              {inviteLink}
            </div>
            <button onClick={handleCopy} style={{
              width: '100%', padding: '11px 0', borderRadius: 8, fontSize: 13, fontWeight: 600,
              background: copied ? 'var(--green)' : 'var(--accent)', color: '#fff',
              border: 'none', cursor: 'pointer', marginBottom: 10, transition: 'background 0.15s',
            }}>
              {copied ? '✓ Copied!' : 'Copy Invite Link'}
            </button>
            <div style={{ fontSize: 11, color: 'var(--text3)', textAlign: 'center', marginBottom: 16 }}>
              Link expires in 7 days
            </div>
            <button onClick={onClose} style={{
              width: '100%', padding: '9px 0', borderRadius: 8, fontSize: 13,
              background: 'none', border: '1px solid var(--border)', color: 'var(--text)', cursor: 'pointer',
            }}>
              Done
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            {error && (
              <div style={{ background: 'var(--red-bg)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 7, padding: '9px 12px', fontSize: 12, color: 'var(--red)', marginBottom: 14 }}>
                {error}
              </div>
            )}

            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 12, color: 'var(--text3)', marginBottom: 5 }}>Email Address</label>
              <input style={inputStyle} type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="colleague@company.com" required autoFocus />
            </div>

            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', fontSize: 12, color: 'var(--text3)', marginBottom: 8 }}>Role</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {['admin', 'member', 'auditor'].map(r => (
                  <label key={r} style={{
                    display: 'flex', gap: 12, padding: '11px 14px', borderRadius: 8, cursor: 'pointer',
                    border: `1px solid ${role === r ? 'rgba(45,125,210,0.4)' : 'var(--border)'}`,
                    background: role === r ? 'var(--accent-glow)' : 'var(--bg2)',
                    transition: 'all 0.15s',
                  }}>
                    <input type="radio" name="role" value={r} checked={role === r} onChange={() => setRole(r)} style={{ marginTop: 2 }} />
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
                        <RoleBadge role={r} />
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--text3)', lineHeight: 1.5 }}>{ROLE_DESCRIPTIONS[r]}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              <button type="button" onClick={onClose} style={{ flex: 1, padding: '10px 0', borderRadius: 8, fontSize: 13, background: 'none', border: '1px solid var(--border)', color: 'var(--text)', cursor: 'pointer' }}>Cancel</button>
              <button type="submit" disabled={inviting || seatsFull} style={{
                flex: 2, padding: '10px 0', borderRadius: 8, fontSize: 13, fontWeight: 600,
                background: 'var(--accent)', color: '#fff', border: 'none', cursor: 'pointer',
                opacity: (inviting || seatsFull) ? 0.7 : 1,
              }}>
                {inviting ? 'Sending…' : 'Send Invite'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

// ── Main export ────────────────────────────────────────────────────────────────

export default function CompanySettings({ company: companyProp, companyMembers: membersProp, session, profile, userPlan, onUpdate }) {
  const [company, setCompany]         = useState(companyProp);
  const [members, setMembers]         = useState(membersProp || []);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [tab, setTab]                 = useState('team');
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [removingId, setRemovingId]   = useState(null);
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileSaved, setProfileSaved]   = useState(false);
  const [profileError, setProfileError]   = useState('');
  const [copiedId, setCopiedId]       = useState(null);
  const [revokingId, setRevokingId]   = useState(null);
  const [changingRole, setChangingRole] = useState(null);
  // Company profile form
  const [form, setForm] = useState({
    name:                    company?.name                    || '',
    company_email:           company?.company_email           || '',
    company_phone:           company?.company_phone           || '',
    company_website:         company?.company_website         || '',
    cage_code:               company?.cage_code               || '',
    ein:                     company?.ein                     || '',
    // SSP fields
    system_name:             company?.system_name             || '',
    system_type:             company?.system_type             || '',
    operating_environment:   company?.operating_environment   || '',
    system_boundary:         company?.system_boundary         || '',
    system_owner_name:       company?.system_owner_name       || '',
    system_owner_title:      company?.system_owner_title      || '',
    isso_name:               company?.isso_name               || '',
    isso_email:              company?.isso_email              || '',
    authorizing_official:    company?.authorizing_official    || '',
    document_version:        company?.document_version        || '1.0',
    next_review_date:        company?.next_review_date        || '',
    reviewed_by:             company?.reviewed_by             || '',
    approved_by:             company?.approved_by             || '',
  });

  const isOwner = company?.owner_id === session?.user?.id;
  const canManage = isOwner || (members.find(m => m.user_id === session?.user?.id)?.role === 'admin');
  const myRole = isOwner ? 'owner' : (members.find(m => m.user_id === session?.user?.id)?.role || 'member');

  const seatLimit = getSeatLimit(userPlan);
  const activeMembers = members.filter(m => m.status === 'active' || m.invite_accepted);
  const seatUsed = activeMembers.length;
  const seatPct  = Math.round((seatUsed / Math.max(seatLimit, 1)) * 100);
  const seatsFull = seatLimit !== 999 && seatUsed >= seatLimit;

  useEffect(() => {
    setMembers(membersProp || []);
  }, [membersProp]);

  useEffect(() => {
    if (company?.id && canManage) loadPendingInvites();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company?.id, canManage]);

  useEffect(() => {
    setForm({
      name:                    company?.name                    || '',
      company_email:           company?.company_email           || '',
      company_phone:           company?.company_phone           || '',
      company_website:         company?.company_website         || '',
      cage_code:               company?.cage_code               || '',
      ein:                     company?.ein                     || '',
      // SSP fields
      system_name:             company?.system_name             || '',
      system_type:             company?.system_type             || '',
      operating_environment:   company?.operating_environment   || '',
      system_boundary:         company?.system_boundary         || '',
      system_owner_name:       company?.system_owner_name       || '',
      system_owner_title:      company?.system_owner_title      || '',
      isso_name:               company?.isso_name               || '',
      isso_email:              company?.isso_email              || '',
      authorizing_official:    company?.authorizing_official    || '',
      document_version:        company?.document_version        || '1.0',
      next_review_date:        company?.next_review_date        || '',
      reviewed_by:             company?.reviewed_by             || '',
      approved_by:             company?.approved_by             || '',
    });
  }, [company]);

  async function loadPendingInvites() {
    if (!company?.id) return;
    const invites = await getCompanyInvites(company.id);
    setPendingInvites(invites || []);
  }

  async function refreshMembers() {
    if (!company?.id) return;
    const m = await getCompanyMembers(company.id);
    setMembers(m || []);
    await loadPendingInvites();
    if (onUpdate) onUpdate();
  }

  async function ensureCompany() {
    if (company) return company;
    const co = await getOrCreateCompany(session.user.id, profile?.company_name || 'My Company');
    setCompany(co);
    return co;
  }

  async function handleRemove(member) {
    if (!window.confirm(`Remove ${member.profile?.email || member.invited_email}? They will lose access.`)) return;
    setRemovingId(member.id);
    await removeMember(member.id);
    logActivity(session?.user?.id, company.id, 'member_removed', 'user', { email: member.profile?.email || member.invited_email });
    await refreshMembers();
    setRemovingId(null);
  }

  async function handleRoleChange(member, newRole) {
    setChangingRole(member.id);
    await updateMemberRole(member.id, newRole);
    logActivity(session?.user?.id, company.id, 'role_changed', 'user', { email: member.profile?.email, new_role: newRole });
    await refreshMembers();
    setChangingRole(null);
  }

  async function handleRevoke(inviteId) {
    if (!window.confirm('Revoke this invite link? The recipient will no longer be able to join.')) return;
    setRevokingId(inviteId);
    await revokeCompanyInvite(inviteId);
    setPendingInvites(prev => prev.filter(i => i.id !== inviteId));
    setRevokingId(null);
  }

  function handleCopyLink(token) {
    const url = `${window.location.origin}?company_invite=${token}`;
    navigator.clipboard.writeText(url).catch(() => {});
    setCopiedId(token);
    setTimeout(() => setCopiedId(null), 2500);
  }

  async function handleSaveProfile(e) {
    e.preventDefault();
    setSavingProfile(true);
    setProfileError('');
    const co = await ensureCompany();
    if (!co) { setSavingProfile(false); setProfileError('Could not save. Try again.'); return; }
    const { error } = await supabase
      .from('companies')
      .update({ ...form, updated_at: new Date().toISOString() })
      .eq('id', co.id);
    if (!error) {
      setCompany(prev => ({ ...prev, ...form }));
      setProfileSaved(true);
      setTimeout(() => setProfileSaved(false), 2500);
      if (onUpdate) onUpdate();
    } else {
      setProfileError('Save failed. Please try again.');
    }
    setSavingProfile(false);
  }

  // ── Member (simplified) view ────────────────────────────────────────────────
  if (!canManage) {
    return (
      <div style={{ padding: '28px 32px', maxWidth: 700 }}>
        <div style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 22, fontWeight: 700, marginBottom: 4 }}>{company?.name || 'My Team'}</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
            <span style={{ fontSize: 12, color: 'var(--text3)' }}>Your role:</span>
            <RoleBadge role={myRole} />
          </div>
        </div>

        {company?.company_code && (
          <div style={{ marginBottom: 20 }}>
            <span style={{ fontSize: 11, color: 'var(--text3)' }}>Company Code: </span>
            <span style={{ fontSize: 12, fontFamily: 'var(--mono)', fontWeight: 600, color: 'var(--text)', letterSpacing: '0.05em' }}>
              {company.company_code}
            </span>
          </div>
        )}

        <div className="glass-card" style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', fontSize: 13, fontWeight: 600 }}>
            Team Members ({activeMembers.length})
          </div>
          {activeMembers.length === 0 ? (
            <div style={{ padding: '32px 20px', textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>No teammates yet.</div>
          ) : (
            activeMembers.map((m, i) => {
              const email = m.profile?.email || m.invited_email || '—';
              const isMe  = m.user_id === session?.user?.id;
              return (
                <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 20px', borderBottom: i < activeMembers.length - 1 ? '1px solid var(--border)' : 'none' }}>
                  <Avatar name={email} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 500 }}>{email}{isMe ? <span style={{ fontSize: 10, color: 'var(--text3)', marginLeft: 6 }}>you</span> : ''}</div>
                    {m.profile?.company_name && <div style={{ fontSize: 11, color: 'var(--text3)' }}>{m.profile.company_name}</div>}
                  </div>
                  <RoleBadge role={m.role} />
                </div>
              );
            })
          )}
        </div>
      </div>
    );
  }

  // ── Owner / Admin view ──────────────────────────────────────────────────────
  return (
    <div style={{ padding: '28px 32px', maxWidth: 1000, margin: '0 auto' }}>

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <div style={{ fontSize: 24, fontWeight: 700, letterSpacing: '-0.01em' }}>
              {company?.name || 'Team Management'}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 8, alignItems: 'center' }}>
              {company?.company_code && (
                <span style={{
                  fontSize: 11, fontFamily: 'var(--mono)', fontWeight: 600, padding: '3px 9px', borderRadius: 6,
                  color: 'var(--text2)', background: 'var(--surface2)', border: '1px solid var(--border)',
                  letterSpacing: '0.04em',
                }}>
                  Company Code: {company.company_code}
                </span>
              )}
              <span style={{ fontSize: 11, padding: '3px 9px', borderRadius: 6, fontWeight: 700, fontFamily: 'var(--mono)', textTransform: 'uppercase', color: 'var(--accent)', background: 'var(--blue-bg)', border: '1px solid rgba(45,125,210,0.25)' }}>
                {userPlan} plan
              </span>
            </div>
          </div>
          {canManage && (
            <TrialGate feature="team_invite" profile={profile} onUpgrade={onUpdate}>
              <button
                onClick={() => setShowInviteModal(true)}
                disabled={seatsFull}
                title={seatsFull ? `Seat limit reached (${seatLimit}/${seatLimit})` : 'Invite a team member'}
                style={{
                  padding: '10px 20px', borderRadius: 8, fontSize: 13, fontWeight: 600,
                  background: seatsFull ? 'var(--surface2)' : 'var(--accent)',
                  color: seatsFull ? 'var(--text3)' : '#fff',
                  border: seatsFull ? '1px solid var(--border)' : 'none',
                  cursor: seatsFull ? 'not-allowed' : 'pointer',
                }}
              >
                + Invite Member
              </button>
            </TrialGate>
          )}
        </div>

        {/* Seat bar */}
        <div style={{ marginTop: 16, maxWidth: 360 }}>
          <SeatBar used={seatUsed} limit={seatLimit} />
        </div>
      </div>

      {/* ── Seat upgrade banner at 80%+ ──────────────────────────────────── */}
      {seatPct >= 80 && seatLimit < 999 && (
        <div style={{
          background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.25)',
          borderRadius: 10, padding: '12px 18px', marginBottom: 20, fontSize: 13,
          display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
        }}>
          <span style={{ fontSize: 18 }}>⚠️</span>
          <div style={{ flex: 1 }}>
            <span style={{ fontWeight: 600, color: 'var(--amber)' }}>
              {seatPct >= 100 ? 'Seat limit reached.' : `${seatUsed}/${seatLimit} seats used (${seatPct}%).`}
            </span>
            {' '}<span style={{ color: 'var(--text3)' }}>
              Upgrade to Business for unlimited seats.
            </span>
          </div>
        </div>
      )}

      {/* ── Tabs ─────────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: 2, borderBottom: '1px solid var(--border)', marginBottom: 24 }}>
        {[['team', 'Team Members'], ['profile', 'Company Profile']].map(([key, label]) => (
          <button key={key} onClick={() => setTab(key)} style={{
            padding: '9px 18px', background: 'none', border: 'none', cursor: 'pointer',
            fontSize: 13, fontWeight: tab === key ? 600 : 400,
            color: tab === key ? 'var(--accent)' : 'var(--text3)',
            borderBottom: tab === key ? '2px solid var(--accent)' : '2px solid transparent',
            marginBottom: -1,
          }}>
            {label}
          </button>
        ))}
      </div>

      {/* ── Tab: Team Members ────────────────────────────────────────────── */}
      {tab === 'team' && (
        <div>
          {/* Active members table */}
          <div className="glass-card" style={{ padding: 0, overflow: 'hidden', marginBottom: 20 }}>
            <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ fontSize: 14, fontWeight: 600 }}>Team Members ({activeMembers.length})</div>
            </div>

            {activeMembers.length === 0 ? (
              <div style={{ padding: '40px 20px', textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>
                No team members yet. Invite your first teammate.
              </div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: 'var(--bg2)' }}>
                    {['Name / Email', 'Role', 'Status', 'Joined', 'Actions'].map(h => (
                      <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontSize: 11, color: 'var(--text3)', fontWeight: 600, borderBottom: '1px solid var(--border)' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {activeMembers.map((m, i) => {
                    const email   = m.profile?.email || m.invited_email || '—';
                    const isMe    = m.user_id === session?.user?.id;
                    const isOwnerRow = m.role === 'owner';
                    return (
                      <tr key={m.id} style={{ borderBottom: i < activeMembers.length - 1 ? '1px solid var(--border)' : 'none' }}>
                        <td style={{ padding: '12px 16px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <Avatar name={email} size={30} />
                            <div>
                              <div style={{ fontWeight: 500 }}>
                                {email}
                                {isMe && <span style={{ fontSize: 10, color: 'var(--text3)', marginLeft: 6 }}>you</span>}
                              </div>
                              {m.profile?.company_name && (
                                <div style={{ fontSize: 11, color: 'var(--text3)' }}>{m.profile.company_name}</div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          {isOwnerRow || isMe ? (
                            <RoleBadge role={m.role} />
                          ) : (
                            <select
                              value={m.role}
                              disabled={changingRole === m.id}
                              onChange={e => handleRoleChange(m, e.target.value)}
                              style={{
                                ...inputStyle, width: 'auto', padding: '4px 8px', fontSize: 11,
                                fontFamily: 'var(--mono)', fontWeight: 700,
                                cursor: changingRole === m.id ? 'wait' : 'pointer',
                              }}
                            >
                              {['admin', 'member', 'auditor'].map(r => (
                                <option key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</option>
                              ))}
                            </select>
                          )}
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          <StatusBadge active={m.status === 'active' || m.invite_accepted} />
                        </td>
                        <td style={{ padding: '12px 16px', color: 'var(--text3)', fontSize: 11 }}>
                          {m.invite_accepted_at || m.created_at
                            ? new Date(m.invite_accepted_at || m.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                            : '—'}
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          {!isOwnerRow && !isMe && (
                            <button
                              onClick={() => handleRemove(m)}
                              disabled={removingId === m.id}
                              style={{
                                background: 'none', border: '1px solid rgba(239,68,68,0.3)',
                                borderRadius: 5, padding: '3px 10px', fontSize: 11, cursor: 'pointer',
                                color: 'var(--red)', opacity: removingId === m.id ? 0.5 : 1,
                              }}
                            >
                              {removingId === m.id ? 'Removing…' : 'Remove'}
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {/* Pending invites */}
          {pendingInvites.length > 0 && (
            <div className="glass-card" style={{ padding: 0, overflow: 'hidden' }}>
              <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', fontSize: 14, fontWeight: 600 }}>
                Pending Invites ({pendingInvites.length})
              </div>
              {pendingInvites.map((inv, i) => (
                <div key={inv.id} style={{
                  display: 'flex', alignItems: 'center', gap: 12, padding: '12px 20px',
                  borderBottom: i < pendingInvites.length - 1 ? '1px solid var(--border)' : 'none',
                  flexWrap: 'wrap',
                }}>
                  <Avatar name={inv.email} size={30} />
                  <div style={{ flex: 1, minWidth: 180 }}>
                    <div style={{ fontSize: 13, fontWeight: 500 }}>{inv.email}</div>
                    <div style={{ fontSize: 11, color: 'var(--text3)' }}>
                      Expires {new Date(inv.expires_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </div>
                  </div>
                  <RoleBadge role={inv.role} />
                  <StatusBadge active={false} />
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      onClick={() => handleCopyLink(inv.token)}
                      style={{
                        background: copiedId === inv.token ? 'var(--green-bg)' : 'var(--surface2)',
                        border: `1px solid ${copiedId === inv.token ? 'rgba(29,184,122,0.3)' : 'var(--border)'}`,
                        borderRadius: 6, padding: '4px 10px', fontSize: 11, cursor: 'pointer',
                        color: copiedId === inv.token ? 'var(--green)' : 'var(--text2)',
                        transition: 'all 0.15s',
                      }}
                    >
                      {copiedId === inv.token ? '✓ Copied' : 'Copy Link'}
                    </button>
                    <button
                      onClick={() => handleRevoke(inv.id)}
                      disabled={revokingId === inv.id}
                      style={{
                        background: 'none', border: '1px solid rgba(239,68,68,0.3)',
                        borderRadius: 6, padding: '4px 10px', fontSize: 11, cursor: 'pointer',
                        color: 'var(--red)', opacity: revokingId === inv.id ? 0.5 : 1,
                      }}
                    >
                      {revokingId === inv.id ? 'Revoking…' : 'Revoke'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Tab: Company Profile (owner only) ────────────────────────────── */}
      {tab === 'profile' && (
        <div style={{ maxWidth: 560 }}>
          {!isOwner && (
            <div style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.2)', borderRadius: 8, padding: '10px 14px', fontSize: 12, color: 'var(--amber)', marginBottom: 20 }}>
              Only the company owner can edit company profile settings.
            </div>
          )}
          <form onSubmit={isOwner ? handleSaveProfile : e => e.preventDefault()}>
            <div className="glass-card" style={{ padding: '22px 24px' }}>
              <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 18 }}>Company Profile</div>

              {[
                { key: 'name',            label: 'Company Name',   placeholder: 'Acme Defense LLC', required: true },
                { key: 'company_email',   label: 'Email',          placeholder: 'contact@acme.com', type: 'email' },
                { key: 'company_phone',   label: 'Phone',          placeholder: '+1 (555) 000-0000' },
                { key: 'company_website', label: 'Website',        placeholder: 'https://acme.com' },
                { key: 'cage_code',       label: 'CAGE Code',      placeholder: '1A2B3', maxLength: 5 },
                { key: 'ein',             label: 'EIN',            placeholder: 'XX-XXXXXXX' },
              ].map(({ key, label, placeholder, type, required, maxLength }) => (
                <div key={key} style={{ marginBottom: 14 }}>
                  <label style={{ display: 'block', fontSize: 12, color: 'var(--text3)', marginBottom: 5 }}>
                    {label}{required ? ' *' : ''}
                  </label>
                  <input
                    style={{ ...inputStyle, background: isOwner ? 'var(--bg2)' : 'var(--surface2)' }}
                    type={type || 'text'}
                    value={form[key]}
                    onChange={e => setForm(prev => ({ ...prev, [key]: e.target.value }))}
                    placeholder={placeholder}
                    required={required}
                    maxLength={maxLength}
                    readOnly={!isOwner}
                  />
                </div>
              ))}

              {/* ── System Security Plan section ──────────────────────────── */}
              <div style={{ borderTop: '1px solid var(--border2)', marginTop: 22, paddingTop: 22 }}>
                <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 6 }}>System Security Plan</div>
                <div style={{ fontSize: 13, color: 'var(--text3)', marginBottom: 20, lineHeight: 1.5 }}>
                  This information will be included in your exported SSP and POA&M documents.
                </div>

                {/* System Name */}
                <div style={{ marginBottom: 14 }}>
                  <label style={{ display: 'block', fontSize: 12, color: 'var(--text3)', marginBottom: 5 }}>
                    System Name
                  </label>
                  <input
                    style={{ ...inputStyle, background: isOwner ? 'var(--bg2)' : 'var(--surface2)' }}
                    type="text"
                    value={form.system_name}
                    onChange={e => setForm(prev => ({ ...prev, system_name: e.target.value }))}
                    placeholder="Official name of the information system"
                    readOnly={!isOwner}
                  />
                  <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>
                    The formal name used to identify this system in official documentation
                  </div>
                </div>

                {/* System Type + Operating Environment */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
                  <div>
                    <label style={{ display: 'block', fontSize: 12, color: 'var(--text3)', marginBottom: 5 }}>
                      System Type
                    </label>
                    <select
                      style={{ ...inputStyle, background: isOwner ? 'var(--bg2)' : 'var(--surface2)', cursor: isOwner ? 'pointer' : 'default' }}
                      value={form.system_type}
                      onChange={e => setForm(prev => ({ ...prev, system_type: e.target.value }))}
                      disabled={!isOwner}
                    >
                      <option value="">Select system type</option>
                      <option value="Major Application">Major Application</option>
                      <option value="General Support System">General Support System</option>
                      <option value="Minor Application">Minor Application</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 12, color: 'var(--text3)', marginBottom: 5 }}>
                      Operating Environment
                    </label>
                    <select
                      style={{ ...inputStyle, background: isOwner ? 'var(--bg2)' : 'var(--surface2)', cursor: isOwner ? 'pointer' : 'default' }}
                      value={form.operating_environment}
                      onChange={e => setForm(prev => ({ ...prev, operating_environment: e.target.value }))}
                      disabled={!isOwner}
                    >
                      <option value="">Select environment</option>
                      <option value="Cloud">Cloud</option>
                      <option value="On-Premise">On-Premise</option>
                      <option value="Hybrid">Hybrid</option>
                    </select>
                  </div>
                </div>

                {/* System Boundary */}
                <div style={{ marginBottom: 14 }}>
                  <label style={{ display: 'block', fontSize: 12, color: 'var(--text3)', marginBottom: 5 }}>
                    System Boundary Description
                  </label>
                  <textarea
                    style={{
                      ...inputStyle,
                      background: isOwner ? 'var(--bg2)' : 'var(--surface2)',
                      resize: 'vertical',
                      minHeight: 110,
                      lineHeight: 1.55,
                    }}
                    rows={6}
                    value={form.system_boundary}
                    onChange={e => setForm(prev => ({ ...prev, system_boundary: e.target.value }))}
                    placeholder="Describe the systems, networks, assets, and data that are within scope of this security plan. Include all hardware, software, data flows, and interconnections."
                    readOnly={!isOwner}
                  />
                  <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>
                    This narrative will appear verbatim in Section 1 of your exported SSP
                  </div>
                </div>

                {/* System Owner Name + Title */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
                  <div>
                    <label style={{ display: 'block', fontSize: 12, color: 'var(--text3)', marginBottom: 5 }}>
                      System Owner Name
                    </label>
                    <input
                      style={{ ...inputStyle, background: isOwner ? 'var(--bg2)' : 'var(--surface2)' }}
                      type="text"
                      value={form.system_owner_name}
                      onChange={e => setForm(prev => ({ ...prev, system_owner_name: e.target.value }))}
                      placeholder="Full name"
                      readOnly={!isOwner}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 12, color: 'var(--text3)', marginBottom: 5 }}>
                      System Owner Title
                    </label>
                    <input
                      style={{ ...inputStyle, background: isOwner ? 'var(--bg2)' : 'var(--surface2)' }}
                      type="text"
                      value={form.system_owner_title}
                      onChange={e => setForm(prev => ({ ...prev, system_owner_title: e.target.value }))}
                      placeholder="Job title"
                      readOnly={!isOwner}
                    />
                  </div>
                </div>

                {/* ISSO Name + Email */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
                  <div>
                    <label style={{ display: 'block', fontSize: 12, color: 'var(--text3)', marginBottom: 5 }}>
                      ISSO Name
                    </label>
                    <input
                      style={{ ...inputStyle, background: isOwner ? 'var(--bg2)' : 'var(--surface2)' }}
                      type="text"
                      value={form.isso_name}
                      onChange={e => setForm(prev => ({ ...prev, isso_name: e.target.value }))}
                      placeholder="Information System Security Officer"
                      readOnly={!isOwner}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 12, color: 'var(--text3)', marginBottom: 5 }}>
                      ISSO Email
                    </label>
                    <input
                      style={{ ...inputStyle, background: isOwner ? 'var(--bg2)' : 'var(--surface2)' }}
                      type="email"
                      value={form.isso_email}
                      onChange={e => setForm(prev => ({ ...prev, isso_email: e.target.value }))}
                      placeholder="isso@company.com"
                      readOnly={!isOwner}
                    />
                  </div>
                </div>

                {/* Authorizing Official */}
                <div style={{ marginBottom: 14 }}>
                  <label style={{ display: 'block', fontSize: 12, color: 'var(--text3)', marginBottom: 5 }}>
                    Authorizing Official
                  </label>
                  <input
                    style={{ ...inputStyle, background: isOwner ? 'var(--bg2)' : 'var(--surface2)' }}
                    type="text"
                    value={form.authorizing_official}
                    onChange={e => setForm(prev => ({ ...prev, authorizing_official: e.target.value }))}
                    placeholder="Name of the person authorized to operate this system"
                    readOnly={!isOwner}
                  />
                </div>

                {/* Document Version + Next Review Date */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
                  <div>
                    <label style={{ display: 'block', fontSize: 12, color: 'var(--text3)', marginBottom: 5 }}>
                      Document Version
                    </label>
                    <input
                      style={{ ...inputStyle, background: isOwner ? 'var(--bg2)' : 'var(--surface2)' }}
                      type="text"
                      value={form.document_version}
                      onChange={e => setForm(prev => ({ ...prev, document_version: e.target.value }))}
                      placeholder="1.0"
                      readOnly={!isOwner}
                    />
                    <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>
                      Increment manually when you make significant changes (e.g. 1.0 &rarr; 1.1 &rarr; 2.0)
                    </div>
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 12, color: 'var(--text3)', marginBottom: 5 }}>
                      Next Review Date
                    </label>
                    <input
                      style={{ ...inputStyle, background: isOwner ? 'var(--bg2)' : 'var(--surface2)' }}
                      type="date"
                      value={form.next_review_date}
                      onChange={e => setForm(prev => ({ ...prev, next_review_date: e.target.value }))}
                      readOnly={!isOwner}
                    />
                  </div>
                </div>

                {/* Reviewed By + Approved By */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
                  <div>
                    <label style={{ display: 'block', fontSize: 12, color: 'var(--text3)', marginBottom: 5 }}>
                      Reviewed By
                    </label>
                    <input
                      style={{ ...inputStyle, background: isOwner ? 'var(--bg2)' : 'var(--surface2)' }}
                      type="text"
                      value={form.reviewed_by}
                      onChange={e => setForm(prev => ({ ...prev, reviewed_by: e.target.value }))}
                      placeholder="Name of reviewer"
                      readOnly={!isOwner}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 12, color: 'var(--text3)', marginBottom: 5 }}>
                      Approved By
                    </label>
                    <input
                      style={{ ...inputStyle, background: isOwner ? 'var(--bg2)' : 'var(--surface2)' }}
                      type="text"
                      value={form.approved_by}
                      onChange={e => setForm(prev => ({ ...prev, approved_by: e.target.value }))}
                      placeholder="Name of approver"
                      readOnly={!isOwner}
                    />
                  </div>
                </div>
              </div>

              {profileError && (
                <div style={{ fontSize: 12, color: 'var(--red)', marginBottom: 12 }}>{profileError}</div>
              )}

              {isOwner && (
                <button
                  type="submit"
                  disabled={savingProfile}
                  style={{
                    padding: '10px 24px', borderRadius: 8, fontSize: 13, fontWeight: 600,
                    background: profileSaved ? 'var(--green)' : 'var(--accent)',
                    color: '#fff', border: 'none', cursor: 'pointer',
                    opacity: savingProfile ? 0.7 : 1, transition: 'background 0.2s',
                  }}
                >
                  {savingProfile ? 'Saving…' : profileSaved ? '✓ Saved' : 'Save Changes'}
                </button>
              )}
            </div>
          </form>
        </div>
      )}

      {/* Invite modal */}
      {showInviteModal && (
        <InviteModal
          company={company}
          session={session}
          profile={profile}
          seatsFull={seatsFull}
          onClose={() => setShowInviteModal(false)}
          onInvited={refreshMembers}
        />
      )}
    </div>
  );
}
